/**
 * ShortcutHint — one-time discovery bubble that teaches new users about `?`.
 *
 * Rules:
 * - Shows only once per browser (localStorage key `ragnes_shortcuts_hint_seen`).
 * - Appears 4 s after mount to avoid competing with post-login toasts/banners.
 * - Auto-dismisses when the user actually presses `?` (the modal takes over).
 * - Manual dismiss via the X button also marks it seen forever.
 * - Respects `prefers-reduced-motion` via the shared `.hint-bubble` keyframes.
 */
import { useEffect, useState } from "react";
import { X, Keyboard } from "lucide-react";

const LS_KEY = "ragnes_shortcuts_hint_seen";

export default function ShortcutHint() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(LS_KEY) === "1") return;
    } catch { /* private mode — fall through and show once per session */ }
    const t = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible) return;
    // If the user presses `?` while the hint is up, treat it as "learned"
    // and let the KeyboardShortcuts modal take the stage.
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (typing) return;
      if (e.key === "?" || (e.key === "/" && e.shiftKey)) dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible]);

  const dismiss = () => {
    try { localStorage.setItem(LS_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="shortcut-hint-bubble"
      className="hint-bubble fixed bottom-6 left-64 z-[9990] max-w-xs rounded-lg border border-sky-500/40 bg-popover/95 shadow-xl backdrop-blur-sm px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-sky-500/15 border border-sky-500/40 flex items-center justify-center shrink-0">
          <Keyboard className="w-3.5 h-3.5 text-sky-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Move faster with your keyboard</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Press{" "}
            <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-border/80 bg-muted">?</kbd>
            {" "}anywhere to see every shortcut.
          </p>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss hint"
          className="text-muted-foreground hover:text-foreground shrink-0 -mr-1 -mt-1 p-1"
          data-testid="shortcut-hint-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
